import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  createMerchantOperator,
  getMerchantStaff,
  getMerchantStaffMember,
  updateMerchantOperator,
} from "../../api/merchantStaffApi";
import ModuleHeader, { ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./staff-form.scss";

const emptyForm = {
  name: "",
  phone: "60",
  role: "Operator",
  outletIds: [],
  status: "Active",
  password: "",
  passwordConfirmation: "",
};

export default function StaffFormPage({ mode = "create" }) {
  const navigate = useNavigate();
  const { staffId, staffType = "operator" } = useParams();
  const isEdit = mode === "edit";
  const ownerAccount = isEdit && staffType === "owner";
  const [form, setForm] = useState(emptyForm);
  const [outletOptions, setOutletOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const listing = await getMerchantStaff();
        setOutletOptions(listing.outlets || []);

        if (isEdit) {
          const member = await getMerchantStaffMember(staffType, staffId);
          setForm({
            ...emptyForm,
            name: member.name || "",
            phone: member.phone || "60",
            role: member.role,
            outletIds: member.outlets?.map((outlet) => outlet.id) || [],
            status: Number(member.status) === 1 ? "Active" : "Inactive",
          });
        } else if (listing.outlets?.length) {
          setForm((current) => ({ ...current, outletIds: [listing.outlets[0].id] }));
        }
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, staffId, staffType]);

  const updateField = (field, value) => {
    setMessage("");
    setError("");
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleOutlet = (outletId) => {
    setForm((current) => ({
      ...current,
      outletIds: current.outletIds.includes(outletId)
        ? current.outletIds.filter((id) => id !== outletId)
        : [...current.outletIds, outletId],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!form.outletIds.length) {
      setError("Select at least one outlet for this Operator.");
      return;
    }
    if ((!isEdit || showPasswordReset) && form.password.length < 8) {
      setError("Password must contain at least 8 characters.");
      return;
    }
    if ((!isEdit || showPasswordReset) && form.password !== form.passwordConfirmation) {
      setError("The password confirmation does not match.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      phone: String(form.phone).replace(/\D/g, ""),
      status: form.status === "Active" ? 1 : 0,
      outlet_ids: form.outletIds,
      password: !isEdit || showPasswordReset ? form.password : null,
      password_confirmation: !isEdit || showPasswordReset ? form.passwordConfirmation : null,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateMerchantOperator(staffId, payload);
        setMessage("Operator updated successfully.");
      } else {
        await createMerchantOperator(payload);
        navigate("/staff");
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ModulePageShell className="staff-form">
        <div className="staff-form__card">Loading staff account...</div>
      </ModulePageShell>
    );
  }

  return (
    <ModulePageShell className="staff-form">
      <ModuleHeader
        bleed
        title={ownerAccount ? "Merchant Owner" : isEdit ? "Edit Operator" : "Add Operator"}
        description={ownerAccount ? "Review the Merchant Owner account created by Platform Admin." : "Create or update an Operator account and assign outlet access."}
        back={
          <ModuleHeaderBack onClick={() => navigate("/staff")}>
            ← Back
          </ModuleHeaderBack>
        }
      />

      <form className="staff-form__card" onSubmit={handleSubmit}>
        <div className="staff-form__header">
          <div><h3>Staff Details</h3><p>{ownerAccount ? "Merchant Owner accounts are created and managed by Platform Admin." : "Merchant Owners can create and manage Operator accounts."}</p></div>
          <span className={`staff-form__badge staff-form__badge--${form.role.toLowerCase()}`}>{form.role}</span>
        </div>

        {error ? <div className="staff-form__password-message staff-form__password-message--error">{error}</div> : null}
        {message ? <div className="staff-form__success">{message}</div> : null}

        {ownerAccount ? (
          <>
            <div className="staff-form__owner-profile">
              <div className="staff-form__owner-avatar">{form.name.charAt(0)}</div>
              <div className="staff-form__owner-identity"><span>Owner account</span><strong>{form.name}</strong><p>This Merchant Owner account is created and managed by Platform Admin.</p></div>
            </div>
            <div className="staff-form__owner-summary">
              <div className="staff-form__summary-item"><span>Login Phone Number</span><strong>+{form.phone}</strong><small>Used to log in to the merchant portal</small></div>
              <div className="staff-form__summary-item"><span>Role</span><strong>Owner</strong><small>Full merchant account access</small></div>
              <div className="staff-form__summary-item"><span>Outlet Access</span><strong>All Outlets</strong><small>New outlets are included automatically</small></div>
              <div className="staff-form__summary-item"><span>Status</span><strong className={`staff-form__status staff-form__status--${form.status.toLowerCase()}`}>{form.status}</strong><small>Managed by platform admin</small></div>
            </div>
            <div className="staff-form__owner-notice">To change this Owner account, please contact the platform administrator.</div>
          </>
        ) : (
          <div className="staff-form__grid">
            <label><span>Full Name</span><input value={form.name} onChange={(event) => updateField("name", event.target.value)} required /></label>
            <label className="staff-form__phone-field">
              <span>Login Phone Number</span>
              <PhoneInput country="my" preferredCountries={["my", "sg", "bn", "id"]} enableSearch countryCodeEditable={false} value={form.phone} onChange={(value) => updateField("phone", value)} inputProps={{ name: "phone", required: true, autoComplete: "tel" }} />
              <small>This phone number is used to log in to the merchant portal.</small>
            </label>
            <label><span>Role</span><select value="Operator" disabled><option>Operator</option></select><small>Only Platform Admin can create a Merchant Owner account.</small></label>
            <label><span>Status</span><select value={form.status} onChange={(event) => updateField("status", event.target.value)}><option>Active</option><option>Inactive</option></select></label>

            <section className="staff-form__outlet-field">
              <div className="staff-form__section-heading"><span>Outlet Access</span></div>
              <div className="staff-form__outlet-options">
                {outletOptions.map((outlet) => {
                  const checked = form.outletIds.includes(outlet.id);
                  return (
                    <label className={`staff-form__outlet-option ${checked ? "staff-form__outlet-option--selected" : ""}`} key={outlet.id}>
                      <input type="checkbox" checked={checked} onChange={() => toggleOutlet(outlet.id)} />
                      <span className="staff-form__outlet-copy"><strong>{outlet.name}</strong><small>{checked ? "Assigned" : "Not assigned"}</small></span>
                    </label>
                  );
                })}
              </div>
              <small className="staff-form__field-help">Select one or more outlets this Operator can access.</small>
            </section>
          </div>
        )}

        {!ownerAccount && !isEdit ? (
          <section className="staff-form__password">
            <div className="staff-form__password-heading"><div><h4>Temporary Password</h4><p>Set the Operator's first login password.</p></div></div>
            <div className="staff-form__password-fields">
              <label><span>Password</span><input type="password" value={form.password} onChange={(event) => updateField("password", event.target.value)} autoComplete="new-password" required /></label>
              <label><span>Confirm Password</span><input type="password" value={form.passwordConfirmation} onChange={(event) => updateField("passwordConfirmation", event.target.value)} autoComplete="new-password" required /></label>
            </div>
          </section>
        ) : null}

        {!ownerAccount && isEdit ? (
          <section className="staff-form__password">
            <div className="staff-form__password-heading">
              <div><h4>Reset Password</h4><p>Optionally set a new temporary password.</p></div>
              <button className="staff-form__password-toggle" type="button" onClick={() => setShowPasswordReset((current) => !current)}>{showPasswordReset ? "Cancel Reset" : "Reset Password"}</button>
            </div>
            {showPasswordReset ? (
              <div className="staff-form__password-fields">
                <label><span>New Password</span><input type="password" value={form.password} onChange={(event) => updateField("password", event.target.value)} autoComplete="new-password" /></label>
                <label><span>Confirm New Password</span><input type="password" value={form.passwordConfirmation} onChange={(event) => updateField("passwordConfirmation", event.target.value)} autoComplete="new-password" /></label>
              </div>
            ) : null}
          </section>
        ) : null}

        <div className="staff-form__footer">
          <button className="staff-form__secondary" type="button" onClick={() => navigate("/staff")}>{ownerAccount ? "Back to Staff" : "Cancel"}</button>
          {!ownerAccount ? <button className="staff-form__primary" disabled={saving} type="submit">{saving ? "Saving..." : isEdit ? "Update Staff" : "Create Operator"}</button> : null}
        </div>
      </form>
    </ModulePageShell>
  );
}
